import React, { useState, useEffect, useCallback } from 'react';
import Joyride, { STATUS, EVENTS, ACTIONS } from 'react-joyride';
import { 
  markTourCompleted, 
  TOUR_STYLES, 
  TOUR_LOCALE 
} from '../services/tourService';

/**
 * GuidedTour Component
 * 
 * A reusable tour wrapper that handles:
 * - Manual start via forceRun prop (controlled by parent)
 * - Step skipping (but not tour skipping)
 * - Tour completion callback to parent
 * 
 * Note: Auto-start on first visit is now controlled by the parent component
 * based on server-side has_completed_tour flag.
 * 
 * @param {string} tourId - Unique identifier for this tour
 * @param {array} steps - Array of Joyride step objects
 * @param {boolean} forceRun - Force tour to run (controlled by parent)
 * @param {function} onComplete - Callback when tour completes
 */
const GuidedTour = ({ 
  tourId, 
  steps, 
  forceRun = false, 
  onComplete,
  continuous = true,
}) => {
  const [run, setRun] = useState(false);
  const [stepIndex, setStepIndex] = useState(0);

  // Only run when forceRun is true (controlled by parent)
  useEffect(() => {
    if (forceRun) {
      // Reset and start tour
      setStepIndex(0);
      // Small delay to let page elements render
      const timer = setTimeout(() => {
        setRun(true);
      }, 500);
      return () => clearTimeout(timer);
    } else {
      setRun(false);
    }
  }, [forceRun]);

  const handleJoyrideCallback = useCallback((data) => {
    const { status, type, action, index } = data;

    // Handle tour completion (finished or skipped)
    if ([STATUS.FINISHED, STATUS.SKIPPED].includes(status)) {
      setRun(false);
      // Mark as completed in localStorage for this session (backup)
      markTourCompleted(tourId);
      if (onComplete) onComplete();
      return;
    }

    // Handle step navigation
    if (type === EVENTS.STEP_AFTER) {
      if (action === ACTIONS.NEXT) {
        setStepIndex(index + 1);
      } else if (action === ACTIONS.PREV) {
        setStepIndex(index - 1);
      }
    }

    // Handle step skip (move to next step)
    if (action === ACTIONS.SKIP) {
      // Move to next step instead of ending tour
      if (index < steps.length - 1) {
        setStepIndex(index + 1);
      } else {
        // On last step, complete the tour
        setRun(false);
        markTourCompleted(tourId);
        if (onComplete) onComplete();
      }
    }

    // Handle target not found - skip to next
    if (type === EVENTS.TARGET_NOT_FOUND) {
      if (index < steps.length - 1) {
        setStepIndex(index + 1);
      }
    }
  }, [tourId, steps.length, onComplete]);

  // Don't render if no steps
  if (!steps || steps.length === 0) return null;

  return (
    <Joyride
      steps={steps}
      run={run}
      stepIndex={stepIndex}
      continuous={continuous}
      showSkipButton={true}
      showProgress={true}
      disableOverlayClose={true}
      disableCloseOnEsc={true}
      hideCloseButton={true}
      spotlightClicks={false}
      styles={TOUR_STYLES}
      locale={TOUR_LOCALE}
      callback={handleJoyrideCallback}
      floaterProps={{
        disableAnimation: false,
        styles: {
          floater: {
            filter: 'drop-shadow(0 4px 12px rgba(0,0,0,0.15))',
          },
        },
      }}
    />
  );
};

export default GuidedTour;
